import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import AlarmOnOutlinedIcon from '@mui/icons-material/AlarmOnOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import TimelineIcon from '@mui/icons-material/Timeline';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import * as ETVConstants from '../../ETVConstants';

const POLL_INTERVAL_MS = 15000;

function translated(_key, _fallback) {
  const _value = ETVConstants.trans(_key);
  return _value === `[${_key}]` ? _fallback : _value;
}

function displayLocation(_location) {
  if (!_location) return '';
  if (typeof _location === 'string') return _location;
  return _location.name || _location.address || _location.label || _location.value || '';
}

function locationObject(_location) {
  if (!_location) return {};
  if (typeof _location === 'string') return { address: _location };
  return typeof _location === 'object' ? _location : {};
}

function locationValue(_location, ..._keys) {
  for (const _key of _keys) {
    const _value = _location?.[_key];
    if (_value !== undefined && _value !== null && String(_value).trim()) {
      return String(_value).trim();
    }
  }
  return '';
}

function stateColor(_state) {
  switch (String(_state || '').toUpperCase()) {
    case 'RESOLVED': return 'success';
    case 'HELP_ON_THE_WAY': return 'info';
    case 'ACKNOWLEDGED': return 'warning';
    default: return 'error';
  }
}

function formatDate(_millis) {
  if (!_millis) return '-';
  return new Date(Number(_millis)).toLocaleString();
}

function stateLabel(_state) {
  return String(_state || 'ALARM_ACTIVE').replaceAll('_', ' ');
}

export default function IntercomAlarmOverview({ parentId, devices = [] }) {
  const [_alarms, _setAlarms] = React.useState([]);
  const [_loading, _setLoading] = React.useState(true);
  const [_error, _setError] = React.useState('');
  const [_timeline, _setTimeline] = React.useState(null);
  const [_timelineLoading, _setTimelineLoading] = React.useState(false);
  const [_shownLocation, _setShownLocation] = React.useState(null);

  const fetchJSON = React.useCallback(async (_url) => {
    const _response = await fetch(_url, {
      cache: 'no-store',
      headers: {
        user_id: String(parentId || ''),
        access_token: localStorage.getItem('etv.access_token') || '',
      },
    });
    if (!_response.ok) throw new Error(`HTTP ${_response.status}`);
    const _body = await _response.json();
    if (_body?.result === false) throw new Error(_body.message || 'Request failed');
    return _body;
  }, [parentId]);

  const loadAlarms = React.useCallback(async (_showSpinner = false) => {
    if (!parentId) return;
    if (_showSpinner) _setLoading(true);
    try {
      const _base = (ETVConstants.getServerBase?.() || '').replace(/\/$/, '');
      const _url = `${_base}/intercom_alarm?action=list_active&parent_id=${encodeURIComponent(parentId)}&limit=200`;
      const _body = await fetchJSON(_url);
      _setAlarms(Array.isArray(_body?.alarms) ? _body.alarms : []);
      _setError('');
    } catch (_loadError) {
      _setError(String(_loadError?.message || _loadError));
    } finally {
      _setLoading(false);
    }
  }, [fetchJSON, parentId]);

  React.useEffect(() => {
    loadAlarms(true);
    const _timer = window.setInterval(() => loadAlarms(false), POLL_INTERVAL_MS);
    return () => window.clearInterval(_timer);
  }, [loadAlarms]);

  const openTimeline = async (_alarm) => {
    _setTimeline({ alarm: _alarm, events: [], error: '' });
    _setTimelineLoading(true);
    try {
      const _base = (ETVConstants.getServerBase?.() || '').replace(/\/$/, '');
      const _url = `${_base}/intercom_alarm?action=timeline&parent_id=${encodeURIComponent(parentId)}&session_id=${encodeURIComponent(_alarm.session_id)}`;
      const _body = await fetchJSON(_url);
      _setTimeline({ alarm: _alarm, events: Array.isArray(_body?.events) ? _body.events : [], error: '' });
    } catch (_loadError) {
      _setTimeline({ alarm: _alarm, events: [], error: String(_loadError?.message || _loadError) });
    } finally {
      _setTimelineLoading(false);
    }
  };

  return (
    <>
      <Card sx={{ mb: 3, borderLeft: _alarms.length ? 5 : 0, borderColor: 'error.main' }}>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <AlarmOnOutlinedIcon color={_alarms.length ? 'error' : 'action'} />
              <Typography variant="h6">
                {translated('en81_alarm_overview', 'EN 81-28 alarm overview')}
              </Typography>
              <Chip
                size="small"
                color={_alarms.length ? 'error' : 'default'}
                label={`${_alarms.length} ${translated('active_alarms', 'active')}`}
              />
            </Stack>
            <Button
              size="small"
              startIcon={_loading ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={() => loadAlarms(true)}
              disabled={_loading}
            >
              {translated('refresh', 'Refresh')}
            </Button>
          </Stack>

          {_error && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              {translated('alarm_backend_unavailable', 'Alarm overview is temporarily unavailable. Screens continue operating from their local alarm journal.')}
            </Alert>
          )}

          {!_loading && !_error && _alarms.length === 0 && (
            <Typography color="text.secondary">
              {translated('no_active_alarms', 'No active alarms')}
            </Typography>
          )}

          <Stack spacing={1.25}>
            {_alarms.map((_alarm) => {
              const _linkedDevice = devices.find(
                (_device) => String(_device.device_id) === String(_alarm.device_id)
              );
              const _deviceName = _linkedDevice?.device_name
                || _alarm.device_name
                || _alarm.device_id;
              const _locationData = {
                ...locationObject(_linkedDevice?.location),
                ...locationObject(_alarm.location),
              };
              const _commissionNr = _alarm.commission_nr
                || _alarm.commissionNr
                || _linkedDevice?.commission_nr
                || _locationData.commission_nr
                || '';
              const _location = displayLocation(_locationData);
              const _title = [_location, _deviceName].filter(Boolean).join(' - ')
                || _alarm.friendly_name
                || _alarm.device_id;
              const _communicationLabel = _alarm.communication_connected
                ? translated('call_connected', 'Call connected')
                : String(_alarm.state || '').toUpperCase() === 'DIALING'
                  ? translated('calling', 'Calling')
                  : translated('disconnected', 'Disconnected');
              return (
                <Box key={_alarm.session_id} sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 2 }}>
                  <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5}>
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5, flexWrap: 'wrap' }}>
                        <Typography fontWeight={700}>{_title || '-'}</Typography>
                        <Chip size="small" color={stateColor(_alarm.state)} label={stateLabel(_alarm.state)} />
                        <Chip
                          size="small"
                          variant="outlined"
                          color={_alarm.communication_connected ? 'success' : 'default'}
                          label={_communicationLabel}
                        />
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {translated('alarm_triggered', 'Triggered')}: {formatDate(_alarm.triggered_at_ms)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {translated('commission_nr', 'Commission number')}: {_commissionNr || '-'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {_alarm.active_destination
                          ? `${translated('alarm_contact', 'Contact')}: ${_alarm.active_destination}`
                          : translated('no_active_contact', 'No active contact')}
                      </Typography>
                    </Box>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="stretch">
                      <Button
                        size="small"
                        startIcon={<LocationOnOutlinedIcon />}
                        onClick={() => _setShownLocation({
                          deviceName: _deviceName,
                          commissionNr: _commissionNr,
                          location: _locationData,
                        })}
                      >
                        {translated('show_location', 'Show location')}
                      </Button>
                      <Button size="small" startIcon={<TimelineIcon />} onClick={() => openTimeline(_alarm)}>
                        {translated('alarm_timeline', 'Timeline')}
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </CardContent>
      </Card>

      <Dialog open={!!_timeline} onClose={() => _setTimeline(null)} fullWidth maxWidth="sm">
        <DialogTitle>{translated('alarm_timeline', 'Alarm timeline')}</DialogTitle>
        <DialogContent dividers>
          {_timelineLoading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>}
          {_timeline?.error && <Alert severity="warning">{_timeline.error}</Alert>}
          <Stack spacing={1.25} divider={<Divider flexItem />}>
            {_timeline?.events?.map((_event) => (
              <Box key={_event.event_id}>
                <Typography fontWeight={700}>{stateLabel(_event.event_type)}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatDate(_event.occurred_at_ms)}{_event.destination ? ` · ${_event.destination}` : ''}
                </Typography>
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => _setTimeline(null)}>{translated('close', 'Close')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!_shownLocation} onClose={() => _setShownLocation(null)} fullWidth maxWidth="sm">
        <DialogTitle>{translated('lift_location', 'Lift location')}</DialogTitle>
        <DialogContent dividers>
          {_shownLocation && (() => {
            const _location = _shownLocation.location || {};
            const _name = locationValue(_location, 'name', 'label');
            const _address = locationValue(_location, 'address', 'street_address', 'street');
            const _postalCode = locationValue(_location, 'zip', 'postal_code', 'postcode');
            const _city = locationValue(_location, 'city', 'locality');
            const _state = locationValue(_location, 'state', 'province', 'region');
            const _country = locationValue(_location, 'country', 'country_name');
            const _contactPhone = locationValue(_location, 'contact_phone', 'phone');
            const _contactEmail = locationValue(_location, 'contact_email', 'email');
            const _information = locationValue(_location, 'info', 'information', 'notes', 'description');
            const _latitude = locationValue(_location, 'latitude', 'lat');
            const _longitude = locationValue(_location, 'longitude', 'lgt', 'lng');
            const _latitudeNumber = Number(_latitude);
            const _longitudeNumber = Number(_longitude);
            const _hasValidCoordinates = _latitude !== '' && _longitude !== ''
              && Number.isFinite(_latitudeNumber) && Number.isFinite(_longitudeNumber)
              && _latitudeNumber >= -90 && _latitudeNumber <= 90
              && _longitudeNumber >= -180 && _longitudeNumber <= 180;
            const _mapsUrl = _hasValidCoordinates
              ? `https://www.google.com/maps?q=${encodeURIComponent(`${_latitudeNumber},${_longitudeNumber}`)}`
              : '';
            const _hasLocation = [
              _name, _address, _postalCode, _city, _state, _country,
              _contactPhone, _contactEmail, _information, _latitude, _longitude,
            ]
              .some(Boolean);
            return (
              <Stack spacing={1.25}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {translated('device', 'Device')}
                  </Typography>
                  <Typography fontWeight={700}>{_shownLocation.deviceName || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {translated('commission_nr', 'Commission number')}
                  </Typography>
                  <Typography fontWeight={700}>{_shownLocation.commissionNr || '-'}</Typography>
                </Box>
                {_name && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {translated('location', 'Location')}
                    </Typography>
                    <Typography>{_name}</Typography>
                  </Box>
                )}
                {_address && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {translated('address', 'Address')}
                    </Typography>
                    <Typography>{_address}</Typography>
                  </Box>
                )}
                {[_postalCode, _city].some(Boolean) && (
                  <Typography>{[_postalCode, _city].filter(Boolean).join(' ')}</Typography>
                )}
                {_state && <Typography>{_state}</Typography>}
                {_country && <Typography>{_country}</Typography>}
                {_information && (
                  <Box sx={{ mt: 0.5, p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>
                    <Typography variant="caption" color="text.secondary">
                      {translated('information', 'Information')}
                    </Typography>
                    <Typography sx={{ whiteSpace: 'pre-wrap' }}>{_information}</Typography>
                  </Box>
                )}
                {_contactPhone && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {translated('contact_phone', 'Contact phone')}
                    </Typography>
                    <Typography>{_contactPhone}</Typography>
                  </Box>
                )}
                {_contactEmail && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {translated('email', 'Email')}
                    </Typography>
                    <Typography>{_contactEmail}</Typography>
                  </Box>
                )}
                {(_latitude || _longitude) && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {translated('gps_coordinates', 'GPS coordinates')}
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                      <Typography sx={{ overflowWrap: 'anywhere' }}>
                        {[_latitude, _longitude].filter(Boolean).join(', ')}
                      </Typography>
                      {_mapsUrl && (
                        <Button
                          size="small"
                          variant="outlined"
                          href={_mapsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {translated('open_in_maps', 'Open map')}
                        </Button>
                      )}
                    </Stack>
                  </Box>
                )}
                {!_hasLocation && (
                  <Typography color="text.secondary">
                    {translated('no_location_details', 'No location details available')}
                  </Typography>
                )}
              </Stack>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => _setShownLocation(null)}>{translated('close', 'Close')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
